/**
 * Shared constants and helpers for the RichTextField editor ecosystem.
 *
 * Consumed by: RichTextField, EditorMenus, slash-commands.
 * Pure data — no React components, no side-effects.
 */

import { common, createLowlight } from 'lowlight';
import {
    AlertOctagon,
    AlertTriangle,
    CircleCheck,
    Columns2,
    Columns3,
    Columns4,
    Heading1,
    Heading2,
    Heading3,
    Heading4,
    Heading5,
    Heading6,
    Info,
    Lightbulb,
    ListChecks,
    PanelLeft,
    PanelRight,
    ShieldAlert,
    StickyNote,
} from 'lucide-react';

export const lowlight = createLowlight(common);

/**
 * Validates that a URL uses a safe protocol (http, https, mailto).
 * Rejects javascript:, data:, vbscript: and other dangerous schemes.
 * Used by DocBodyRenderer (read mode) and editor link/image insertion.
 */
export function isSafeUrl(url) {
    if (!url) return false;
    try {
        const u = new URL(url, window.location.origin);
        return ['http:', 'https:', 'mailto:'].includes(u.protocol);
    } catch {
        return false;
    }
}

/** Trims then delegates to {@link isSafeUrl}. Null-safe string guard. */
export function isEditorSafeUrl(url) {
    if (!url || typeof url !== 'string') return false;
    return isSafeUrl(url.trim());
}

/**
 * Converts heading text to a URL-safe anchor id.
 * NFD normalization strips accents; falls back to 'heading' if empty.
 */
export function slugify(text) {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        || 'heading';
}

export const CODE_LANGUAGES = [
    'bash', 'c', 'cpp', 'csharp', 'css', 'diff', 'go', 'graphql',
    'ini', 'java', 'javascript', 'json', 'kotlin', 'less', 'lua',
    'makefile', 'markdown', 'objectivec', 'perl', 'php', 'python',
    'ruby', 'rust', 'scss', 'shell', 'sql', 'swift', 'typescript',
    'xml', 'yaml',
];

export const HEADING_ICONS = [Heading1, Heading2, Heading3, Heading4, Heading5, Heading6];

export const DEFAULT_FEATURES = [
    'bold', 'italic', 'underline', 'strike', 'code',
    'heading', 'textAlign', 'highlight', 'color',
    'bulletList', 'orderedList', 'taskList',
    'blockquote', 'codeBlock', 'horizontalRule',
    'link', 'image', 'table',
    'callout', 'details', 'columns',
    'subscript', 'superscript',
    'mention',
];

export const CALLOUT_TYPES = {
    info:         { icon: <Info size={16} />,           label: 'Info',         classes: 'bg-blue-50 border-blue-200 text-blue-800' },
    tip:          { icon: <Lightbulb size={16} />,      label: 'Astuce',       classes: 'bg-teal-50 border-teal-200 text-teal-800' },
    warning:      { icon: <AlertTriangle size={16} />,  label: 'Attention',    classes: 'bg-amber-50 border-amber-200 text-amber-800' },
    caution:      { icon: <ShieldAlert size={16} />,    label: 'Précaution',   classes: 'bg-orange-50 border-orange-200 text-orange-800' },
    danger:       { icon: <AlertOctagon size={16} />,   label: 'Danger',       classes: 'bg-red-50 border-red-200 text-red-800' },
    success:      { icon: <CircleCheck size={16} />,    label: 'Succès',       classes: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
    note:         { icon: <StickyNote size={16} />,     label: 'Note',         classes: 'bg-stone-100 border-stone-300 text-stone-700' },
    prerequisite: { icon: <ListChecks size={16} />,     label: 'Prérequis',    classes: 'bg-violet-50 border-violet-200 text-violet-800' },
};

export const COLUMN_LAYOUTS = {
    'equal-2':    { cols: 2, label: '2 colonnes',     icon: Columns2 },
    'equal-3':    { cols: 3, label: '3 colonnes',     icon: Columns3 },
    'equal-4':    { cols: 4, label: '4 colonnes',     icon: Columns4 },
    'left-wide':  { cols: 2, label: 'Gauche large',   icon: PanelLeft },
    'right-wide': { cols: 2, label: 'Droite large',   icon: PanelRight },
    'sidebar':    { cols: 2, label: 'Sidebar',        icon: PanelLeft },
};

export const HEADING_LABELS = { 1: 'Titre 1', 2: 'Titre 2', 3: 'Titre 3', 4: 'Titre 4', 5: 'Titre 5', 6: 'Titre 6' };

export const HIGHLIGHT_COLORS = [
    { label: 'Jaune', value: '#fef08a' },
    { label: 'Vert', value: '#bbf7d0' },
    { label: 'Bleu', value: '#bfdbfe' },
    { label: 'Rose', value: '#fbcfe8' },
    { label: 'Orange', value: '#fed7aa' },
];

export const CELL_BG_COLORS = [
    { label: 'Aucun', value: null },
    { label: 'Gris clair', value: '#f5f5f4' },
    { label: 'Jaune', value: '#fef9c3' },
    { label: 'Vert', value: '#dcfce7' },
    { label: 'Bleu', value: '#dbeafe' },
    { label: 'Rose', value: '#fce7f3' },
    { label: 'Orange', value: '#ffedd5' },
    { label: 'Violet', value: '#ede9fe' },
    { label: 'Rouge', value: '#fee2e2' },
];

export const FONT_FAMILIES = [
    { label: 'Défaut', value: '' },
    { label: 'Sans-serif', value: 'system-ui, -apple-system, sans-serif' },
    { label: 'Serif', value: 'Georgia, Cambria, serif' },
    { label: 'Monospace', value: 'ui-monospace, SFMono-Regular, monospace' },
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
];

export const FONT_SIZES = [
    { label: '12', value: '12px' },
    { label: '14', value: '14px' },
    { label: '16', value: '16px' },
    { label: '18', value: '18px' },
    { label: '20', value: '20px' },
    { label: '24', value: '24px' },
    { label: '30', value: '30px' },
    { label: '36', value: '36px' },
    { label: '48', value: '48px' },
];

export const TEXT_COLORS = [
    { label: 'Défaut', value: '' },
    { label: 'Rouge', value: '#dc2626' },
    { label: 'Orange', value: '#ea580c' },
    { label: 'Vert', value: '#16a34a' },
    { label: 'Bleu', value: '#2563eb' },
    { label: 'Violet', value: '#7c3aed' },
    { label: 'Gris', value: '#78716c' },
];
